import { ErrorSidePanel } from '@/components/errors';
import { useForm } from '@deadcow-enterprises/react-forms';
import {
  logger,
  templates,
  type SkinPageConfig,
  useHasRequiredFormError,
} from '@nosinovacao/nosid-mfe-common';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import z from 'zod';
import { useCreateUser } from '../-hooks/create-user.hook';
import { type InviteSuccessState } from '../-model/flow';
import { type AddUserProps } from './-model/props';
import { AddUserLight } from './-skins';

export const Route = createFileRoute('/(services)/services/_sheet/users/add/')({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { mutateAsync: createUser } = useCreateUser();

  const form = useForm(
    {
      name: {
        value: '',
        id: 'add-user-name',
        validator: z
          .string()
          .min(1, 'Services.Users.Add.Errors.NameRequired')
          .regex(/^[^0-9]*$/, 'Services.Users.Add.Errors.NameInvalid')
          .refine(
            (val) => !z.string().emailAddress().safeParse(val).success,
            'Services.Users.Add.Errors.NameInvalid',
          ),
      },
      email: {
        value: '',
        id: 'add-user-email',
        validator: z
          .string()
          .min(1, 'Services.Users.Add.Errors.EmailRequired')
          .emailAddress('Services.Users.Add.Errors.InvalidEmail'),
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );

  async function handleSubmit({ name, email }: { name: string; email: string }) {
    const user = await createUser({
      DisplayName: name,
      Email: email,
      Applications: [],
    });
    const state: InviteSuccessState = {
      email,
      userId: user.Id,
      displayName: user.DisplayName,
    };
    navigate({ to: '/services/users/invite-success', state });
  }

  const hasRequiredFormError = useHasRequiredFormError(form);

  const props: AddUserProps = {
    name: form.value.name,
    nameErrors: form.errors.name ?? [],
    nameInputId: form.ids.name ?? 'add-user-name',
    email: form.value.email,
    emailErrors: form.errors.email ?? [],
    emailInputId: form.ids.email ?? 'add-user-email',
    loading: form.loading,
    disabled: hasRequiredFormError,
    onNameChange: (v) => form.setValue('name', v),
    onEmailChange: (v) => form.setValue('email', v),
    onSubmit: form.handleSubmit(handleSubmit),
  };
  return templates.getSkinSelector(skinConfig(props));
}

function skinConfig(props: AddUserProps): SkinPageConfig {
  return {
    skins: [],
    defaultValue: <AddUserLight {...props} />,
  };
}
